import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type PointerEvent,
} from 'react';
import { useDebounce, useTrackingContext } from '@givelify/utils';
import { EditorContent } from './components/EditorContent';
import { EditorFooter } from './components/EditorFooter';
import { EditorHeader } from './components/EditorHeader';
import {
    AVATAR_CIRCLE_INSET,
    DEFAULT_ZOOM,
    MAX_ZOOM,
    MIN_ZOOM,
    OUTPUT_WIDTH,
    SNAP_THRESHOLD,
    ZOOM_SNAP_THRESHOLD,
    ZOOM_STEP_VALUE,
} from './constants';
import { ImageEditorProvider } from './context/ImageEditorContext';
import { Divider, StyledModal } from './style';
import type {
    BackgroundOptionItem,
    FitSizes,
    ImageEditorApplyResult,
    ImageEditorCopy,
    UploadError,
} from './types';
import {
    BackgroundSelection,
    ImageAnalysisResult,
    PreparedImage,
    prepareImageForEditor,
} from './utils/convertImage';
import {
    getFitSizes,
    getSliderValueFromZoom,
    getZoomFromSliderValue,
} from './utils/editorMath';
import { renderCanvas } from './utils/renderCanvas';

export type ImageEditorProps = {
    aspectRatio: number;
    avatar?: boolean;
    hideBackgroundSelection?: boolean;
    disableZoomOutBeyondCrop?: boolean;
    outputWidth?: number;
    outputHeight?: number;
    imageUrl: string;
    open: boolean;
    isLoading: boolean;
    isUploading?: boolean;
    uploadProgress?: number;
    onClose: () => void;
    onApply?: (result: ImageEditorApplyResult) => void;
    fileName: string;
    copy: ImageEditorCopy;
    uploadError?: UploadError;
    hidden?: boolean;
    name: string;
    additionalTrackingParams?: Record<string, unknown>;
};

export const ImageEditor: React.FC<ImageEditorProps> = ({
    open,
    isLoading,
    isUploading = false,
    uploadProgress,
    onClose,
    onApply,
    fileName,
    copy,
    aspectRatio,
    avatar = false,
    hideBackgroundSelection = false,
    disableZoomOutBeyondCrop = false,
    outputWidth = OUTPUT_WIDTH,
    outputHeight = avatar
        ? OUTPUT_WIDTH
        : Math.round(OUTPUT_WIDTH / aspectRatio),
    imageUrl,
    uploadError,
    hidden,
    name,
    additionalTrackingParams = {},
}) => {
    const { trackEventV2 } = useTrackingContext();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const dragState = useRef<{
        active: boolean;
        startX: number;
        startY: number;
        originX: number;
        originY: number;
    }>({ active: false, startX: 0, startY: 0, originX: 0, originY: 0 });
    const objectUrlRef = useRef<string | undefined>(undefined);
    // Blocks a second apply while the click/encode work is still running.
    const applyInFlightRef = useRef(false);

    const [zoom, setZoom] = useState(1);
    const debouncedZoomChange = useDebounce(zoom, 1000);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [imageData, setImageData] = useState<PreparedImage | null>(null);
    const [analysis, setAnalysis] = useState<ImageAnalysisResult | null>(null);
    const [backgroundSelection, setBackgroundSelection] =
        useState<BackgroundSelection>({ type: 'blur' });
    const [defaultBackground, setDefaultBackground] =
        useState<BackgroundSelection>({ type: 'blur' });
    const [rotation, setRotation] = useState(0);
    const [isRotating, setIsRotating] = useState(false);
    const [containerSize, setContainerSize] = useState({
        width: 0,
        height: 0,
    });
    const [isProcessing, setIsProcessing] = useState(false);
    const rotationTimeoutRef = useRef<number | null>(null);
    const constrainedZoomOffset = 0.2;

    const setZoomWithSnap = useCallback(
        (
            value: number,
            fillZoom: number,
            minimumZoom: number,
            defaultZoom: number,
            disableZoomSnap: boolean,
        ) => {
            let nextZoom = value;
            if (!disableZoomSnap) {
                if (
                    defaultZoom > minimumZoom &&
                    Math.abs(nextZoom - defaultZoom) <= ZOOM_SNAP_THRESHOLD
                ) {
                    nextZoom = defaultZoom;
                }
                if (
                    fillZoom <= MAX_ZOOM &&
                    Math.abs(nextZoom - fillZoom) <= ZOOM_SNAP_THRESHOLD
                ) {
                    nextZoom = fillZoom;
                }
            }
            nextZoom = Math.min(MAX_ZOOM, Math.max(minimumZoom, nextZoom));
            setZoom(nextZoom);
        },
        [],
    );

    useEffect(() => {
        if (debouncedZoomChange === 1) {
            return;
        }

        trackEventV2?.('click', {
            name: 'ZoomAdjust',
            type: 'Slider',
            location: name,
            ...additionalTrackingParams,
        });
        //watch only zoom change
        //eslint-disable-next-line
    }, [debouncedZoomChange]);

    useEffect(() => {
        let isActive = true;
        setIsProcessing(true);
        prepareImageForEditor(imageUrl)
            .then((result) => {
                if (!isActive) {
                    if (result?.objectUrl) {
                        URL.revokeObjectURL(result.objectUrl);
                    }
                    return;
                }
                if (objectUrlRef.current) {
                    URL.revokeObjectURL(objectUrlRef.current);
                    objectUrlRef.current = undefined;
                }
                if (result?.objectUrl) {
                    objectUrlRef.current = result.objectUrl;
                }
                setImageData(result);
                setAnalysis(result?.analysis ?? null);
                setBackgroundSelection(
                    result?.analysis?.defaultBackground ?? { type: 'blur' },
                );
                setDefaultBackground(
                    result?.analysis?.defaultBackground ?? { type: 'blur' },
                );
                setZoom(DEFAULT_ZOOM);
                setRotation(0);
                setOffset({ x: 0, y: 0 });
            })
            .finally(() => {
                if (isActive) {
                    setIsProcessing(false);
                }
            });
        return () => {
            isActive = false;
        };
    }, [imageUrl]);

    useEffect(() => {
        trackEventV2?.('view', {
            name: name,
            type: 'Modal',
            ...additionalTrackingParams,
        });
        return () => {
            if (objectUrlRef.current) {
                URL.revokeObjectURL(objectUrlRef.current);
            }
        };
        // run only once
        //eslint-disable-next-line
    }, []);

    useEffect(() => {
        return () => {
            if (rotationTimeoutRef.current) {
                window.clearTimeout(rotationTimeoutRef.current);
            }
        };
    }, []);

    const updateSize = useCallback(() => {
        if (!containerRef.current) {
            return;
        }
        const { width, height } = containerRef.current.getBoundingClientRect();
        setContainerSize({ width, height });
    }, []);

    useEffect(() => {
        updateSize();
        window.addEventListener('resize', updateSize);
        return () => window.removeEventListener('resize', updateSize);
    }, [updateSize]);

    useEffect(() => {
        if (!open) {
            return;
        }
        const frame = requestAnimationFrame(() => {
            updateSize();
        });
        return () => cancelAnimationFrame(frame);
    }, [open, updateSize, imageData]);

    const avatarViewportSize = useMemo(
        () => Math.max(0, containerSize.height - AVATAR_CIRCLE_INSET * 2),
        [containerSize.height],
    );

    const fitSizes = useMemo<FitSizes>(() => {
        if (!imageData) {
            return {
                baseWidth: 0,
                baseHeight: 0,
                boxWidth: 0,
                boxHeight: 0,
                fitScale: 0,
                fillZoom: DEFAULT_ZOOM,
            };
        }
        return getFitSizes({
            containerWidth: avatar ? avatarViewportSize : containerSize.width,
            containerHeight: avatar ? avatarViewportSize : containerSize.height,
            imageWidth: imageData.width,
            imageHeight: imageData.height,
            rotation,
            defaultZoom: DEFAULT_ZOOM,
        });
    }, [
        avatar,
        containerSize.height,
        containerSize.width,
        imageData,
        rotation,
        avatarViewportSize,
    ]);

    const effectiveMinZoom = useMemo(
        () => (disableZoomOutBeyondCrop ? fitSizes.fillZoom : MIN_ZOOM),
        [disableZoomOutBeyondCrop, fitSizes.fillZoom],
    );

    const effectiveDefaultZoom = useMemo(
        () =>
            disableZoomOutBeyondCrop
                ? Math.min(MAX_ZOOM, effectiveMinZoom + constrainedZoomOffset)
                : DEFAULT_ZOOM,
        [disableZoomOutBeyondCrop, effectiveMinZoom],
    );

    useEffect(() => {
        if (disableZoomOutBeyondCrop && zoom < effectiveMinZoom) {
            setZoom(effectiveMinZoom);
        }
    }, [disableZoomOutBeyondCrop, effectiveMinZoom, zoom]);

    useEffect(() => {
        if (!disableZoomOutBeyondCrop || !imageData) {
            return;
        }

        setZoom(effectiveDefaultZoom);
    }, [disableZoomOutBeyondCrop, effectiveDefaultZoom, imageData]);

    const maxOffsets = useMemo(() => {
        const scaledWidth = fitSizes.boxWidth * zoom;
        const scaledHeight = fitSizes.boxHeight * zoom;
        const viewportWidth = avatar ? avatarViewportSize : containerSize.width;
        const viewportHeight = avatar
            ? avatarViewportSize
            : containerSize.height;

        return {
            maxOffsetX: Math.max(0, (scaledWidth - viewportWidth) / 2),
            maxOffsetY: Math.max(0, (scaledHeight - viewportHeight) / 2),
        };
    }, [
        avatar,
        avatarViewportSize,
        containerSize.height,
        containerSize.width,
        fitSizes,
        zoom,
    ]);

    const constrainOffset = useCallback(
        (nextOffset: { x: number; y: number }) => ({
            x: Math.min(
                maxOffsets.maxOffsetX,
                Math.max(-maxOffsets.maxOffsetX, nextOffset.x),
            ),
            y: Math.min(
                maxOffsets.maxOffsetY,
                Math.max(-maxOffsets.maxOffsetY, nextOffset.y),
            ),
        }),
        [maxOffsets.maxOffsetX, maxOffsets.maxOffsetY],
    );

    const snapOffset = useCallback(
        (nextOffset: { x: number; y: number }) => {
            const snapAxis = (value: number, max: number) => {
                if (Math.abs(value) < SNAP_THRESHOLD) {
                    return 0;
                }
                if (Math.abs(value - max) < SNAP_THRESHOLD) {
                    return max;
                }
                if (Math.abs(value + max) < SNAP_THRESHOLD) {
                    return -max;
                }
                return value;
            };

            return {
                x: snapAxis(nextOffset.x, maxOffsets.maxOffsetX),
                y: snapAxis(nextOffset.y, maxOffsets.maxOffsetY),
            };
        },
        [maxOffsets.maxOffsetX, maxOffsets.maxOffsetY],
    );

    useEffect(() => {
        setOffset((current) => constrainOffset(current));
    }, [constrainOffset, zoom, containerSize, fitSizes]);

    const onPointerDown = useCallback(
        (event: PointerEvent<HTMLDivElement>) => {
            if (!containerRef.current || isUploading) {
                return;
            }
            dragState.current = {
                active: true,
                startX: event.clientX,
                startY: event.clientY,
                originX: offset.x,
                originY: offset.y,
            };
            event.currentTarget.setPointerCapture(event.pointerId);
        },
        [offset.x, offset.y, isUploading],
    );

    const onPointerMove = useCallback(
        (event: PointerEvent<HTMLDivElement>) => {
            if (!dragState.current.active) {
                return;
            }
            const deltaX = event.clientX - dragState.current.startX;
            const deltaY = event.clientY - dragState.current.startY;
            const nextOffset = {
                x: dragState.current.originX + deltaX,
                y: dragState.current.originY + deltaY,
            };
            setOffset(constrainOffset(nextOffset));
        },
        [constrainOffset],
    );

    const onPointerUp = useCallback(
        (event: PointerEvent<HTMLDivElement>) => {
            if (!dragState.current.active) {
                return;
            }
            dragState.current.active = false;
            event.currentTarget.releasePointerCapture(event.pointerId);
            setOffset((current) => {
                const constrainedOffset = constrainOffset(current);

                return disableZoomOutBeyondCrop
                    ? constrainedOffset
                    : snapOffset(constrainedOffset);
            });
        },
        [constrainOffset, disableZoomOutBeyondCrop, snapOffset],
    );

    const sliderValue = useMemo(
        () =>
            getSliderValueFromZoom({
                zoom,
                minZoom: effectiveMinZoom,
                defaultZoom: effectiveDefaultZoom,
                maxZoom: MAX_ZOOM,
            }),
        [effectiveDefaultZoom, effectiveMinZoom, zoom],
    );

    const isBackgroundVisible = zoom < fitSizes.fillZoom - 0.001;
    const dominantColors = useMemo(
        () => analysis?.dominantColors ?? [],
        [analysis?.dominantColors],
    );

    const backgroundOptions: BackgroundOptionItem[] = useMemo(
        () => [
            {
                id: 'blur',
                label: copy.backgroundBlur ?? 'Blur',
                value: { type: 'blur' },
            },
            {
                id: 'primary',
                label: copy.backgroundPrimary ?? 'Primary',
                value: { type: 'color', color: dominantColors[0] },
            },
            {
                id: 'tertiary',
                label: copy.backgroundAccent ?? 'Accent',
                value: { type: 'color', color: dominantColors[1] },
            },
        ],
        [copy.backgroundAccent, copy.backgroundBlur, copy.backgroundPrimary, dominantColors],
    );

    const isResetVisible =
        zoom !== effectiveDefaultZoom ||
        rotation !== 0 ||
        offset.x !== 0 ||
        offset.y !== 0 ||
        backgroundSelection.type !== defaultBackground.type ||
        (backgroundSelection.type === 'color' &&
            defaultBackground.type === 'color' &&
            backgroundSelection.color !== defaultBackground.color);

    const handleReset = () => {
        setZoom(effectiveDefaultZoom);
        setOffset({ x: 0, y: 0 });
        setRotation(0);
        setBackgroundSelection(defaultBackground);
    };

    const handleBackgroundChange = (
        value: BackgroundSelection,
        index: number,
    ) => {
        if (!isBackgroundVisible) {
            return;
        }
        setBackgroundSelection(value);

        const trackEventNames = [
            'BlurBackground',
            'PrimaryBackground',
            'TertiaryBackground',
        ];
        trackEventV2?.('click', {
            name: trackEventNames[index],
            type: 'Button',
            location: name,
            ...additionalTrackingParams,
        });
    };

    const adjustZoom = (delta: number) => {
        setZoomWithSnap(
            zoom + delta,
            fitSizes.fillZoom,
            effectiveMinZoom,
            effectiveDefaultZoom,
            disableZoomOutBeyondCrop,
        );
    };

    const handleZoomChange = useCallback(
        (value: number) => {
            const nextZoom = getZoomFromSliderValue({
                value,
                minZoom: effectiveMinZoom,
                defaultZoom: effectiveDefaultZoom,
                maxZoom: MAX_ZOOM,
            });
            setZoomWithSnap(
                nextZoom,
                fitSizes.fillZoom,
                effectiveMinZoom,
                effectiveDefaultZoom,
                disableZoomOutBeyondCrop,
            );
        },
        [
            disableZoomOutBeyondCrop,
            effectiveDefaultZoom,
            effectiveMinZoom,
            fitSizes.fillZoom,
            setZoomWithSnap,
        ],
    );

    const handleRotate = () => {
        setRotation((prev) => (prev + 90) % 360);
        setIsRotating(true);
        if (rotationTimeoutRef.current) {
            window.clearTimeout(rotationTimeoutRef.current);
        }
        rotationTimeoutRef.current = window.setTimeout(() => {
            setIsRotating(false);
        }, 220);

        trackEventV2?.('click', {
            name: 'Rotate',
            type: 'Button',
            location: name,
            ...additionalTrackingParams,
        });
    };

    const handleClose = () => {
        onClose();

        trackEventV2?.('click', {
            name: 'Cancel',
            type: 'Button',
            location: name,
            ...additionalTrackingParams,
        });
    };

    const handleApply = async () => {
        if (!onApply || !imageData || applyInFlightRef.current) {
            return;
        }
        applyInFlightRef.current = true;
        trackEventV2?.('click', {
            name: 'Save',
            type: 'Button',
            location: name,
            ...additionalTrackingParams,
        });
        try {
            const croppedUrl = await renderCanvas({
                imageData,
                containerWidth: containerSize.width,
                containerHeight: containerSize.height,
                fitScale: fitSizes.fitScale,
                aspectRatio,
                avatar,
                backgroundSelection,
                zoom,
                rotation,
                offsetX: offset.x,
                offsetY: offset.y,
                outputWidth,
                outputHeight,
            });
            if (!croppedUrl) {
                return;
            }
            onApply({
                croppedUrl,
                dimensions: {
                    croppedAreaPixels: {
                        x: 0,
                        y: 0,
                        width: outputWidth,
                        height: outputHeight,
                    },
                },
            });
        } catch {
            return;
        } finally {
            applyInFlightRef.current = false;
        }
    };

    const isBusy = isLoading || isProcessing;
    const clampedUploadProgress =
        typeof uploadProgress === 'number'
            ? Math.min(100, Math.max(0, uploadProgress))
            : 0;

    return (
        <StyledModal
            disableBackdropClose
            showCloseButton
            classes={{ header: 'modal-header' }}
            isHidden={hidden}
            isLoading={isLoading}
            onClose={handleClose}
            open={open}
        >
            <ImageEditorProvider
                value={{
                    copy,
                    aspectRatio,
                    avatar,
                    fileName,
                    processingLabel: copy.processing,
                    isBusy,
                    isUploading,
                    uploadProgress: clampedUploadProgress,
                    uploadError,
                    containerRef,
                    fitSizes,
                    imageData,
                    isRotating,
                    offset,
                    rotation,
                    zoom,
                    backgroundSelection,
                    backgroundOptions,
                    hideBackgroundSelection,
                    isBackgroundVisible,
                    sliderValue,
                    isResetVisible,
                    onPointerDown,
                    onPointerMove,
                    onPointerUp,
                    onBackgroundChange: handleBackgroundChange,
                    onRotate: handleRotate,
                    onZoomChange: handleZoomChange,
                    onZoomMinus: () => adjustZoom(-ZOOM_STEP_VALUE),
                    onZoomPlus: () => adjustZoom(ZOOM_STEP_VALUE),
                    onApply: handleApply,
                    onClose: handleClose,
                    onReset: handleReset,
                    name,
                }}
            >
                <EditorHeader />
                {isBusy ? null : <Divider top />}
                <EditorContent />
                {isBusy ? null : <Divider bottom />}
                <EditorFooter />
            </ImageEditorProvider>
        </StyledModal>
    );
};
